import React, { useMemo, useState, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { useAuth } from '@/hooks/auth';
import { useCashPools } from '@/hooks/cash-pool';
import { useCounterpartiesRegister, type CounterpartyRegisterParams } from '@/hooks/counterparty';
import { useCustodianRegistry } from '@/hooks/custodian';
import { createCounterpartyColumns } from '../components/counterparty-columns';
import { createCustodianColumns } from '../components/custodian-columns';
import { CounterpartyTable } from '../components/CounterpartyTable';
import { CustodianTable } from '../components/CustodianTable';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { AddCounterpartyAgreementModal } from '../components/AddCounterpartyAgreementModal';
import { AddCustodianModal } from '../components/AddCustodianModal';
import { CounterpartyListRow } from '@/types/counterparty.types';
import { CustodianItem } from '@/types';
import { CashPool, CashPoolProduct } from '@/types/cash-pool.types';
import { createCashPoolColumns } from '@/features/collateral/components/cash-pool-columns';
import { CashPoolTable } from '@/features/collateral/components/CashPoolTable';
import { CashPoolModal } from '@/features/collateral/components/CashPoolModal';
import { ProductTabs } from '@/features/collateral/components/ProductTabs';
import { SearchBar } from '@/components/ui/SearchBar';
import { useCounterpartyFilterStore, useUIStore } from '@/stores';
import { useDebouncedValue } from '@/hooks/common';
import { HelpLink } from '@/features/help';
import { TourSentinel } from '@/features/onboarding';

// LR-587 (product answer 10.08): the cash-pool register lives here, next to the
// other reference data — "В Registers нужно будет раздел Cash Pools". The
// Collateral Manager keeps the working view of the collateral; this tab holds
// the pools' terms and names.
const REGISTER_TABS = ['counterparties', 'custodians', 'cash-pools'] as const;
type RegisterTab = (typeof REGISTER_TABS)[number];

const REGISTER_TAB_LABELS: Record<RegisterTab, string> = {
  counterparties: 'Counterparties',
  custodians: 'Custodians',
  'cash-pools': 'Cash Pools',
};

const TAB_BUTTON_CLASSES =
  'px-3 py-1.5 text-lg font-semibold transition-colors whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';

export const CounterpartiesPage: React.FC = () => {
  // LR-441: local tab state (prototype scope — the route carries no search
  // schema, so switching tabs does not push a URL). It is still readable ON
  // ENTRY: the trade form's empty-registry hint links straight at Custodians,
  // and landing on Counterparties instead makes the hint point at the wrong tab.
  const [activeTab, setActiveTab] = useState<RegisterTab>(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    // Matched against REGISTER_TABS rather than one hard-coded name: LR-587 added
    // a third tab, and a hint that links at it must not land on the first one.
    return REGISTER_TABS.find((tab) => tab === requested) ?? 'counterparties';
  });

  // Roving-tabindex + arrow-key navigation across the tablist (WAI-ARIA tabs).
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const handleTabKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const dir = event.key === 'ArrowRight' ? 1 : -1;
    const next = (index + dir + REGISTER_TABS.length) % REGISTER_TABS.length;
    setActiveTab(REGISTER_TABS[next]);
    tabRefs.current[next]?.focus();
  };

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedForEdit, setSelectedForEdit] = useState<CounterpartyListRow | null>(null);

  // Custodian register state (LR-441).
  const [isAddCustodianOpen, setIsAddCustodianOpen] = useState(false);
  const [selectedCustodianForEdit, setSelectedCustodianForEdit] = useState<CustodianItem | null>(null);
  const [custodianSearch, setCustodianSearch] = useState('');

  // Cash-pool register state (LR-580, moved here by LR-587). The GMRA/GMSLA
  // segment is local like the tab itself — the register is reference data, not
  // a shareable working view.
  const [isCreatePoolOpen, setIsCreatePoolOpen] = useState(false);
  const [poolForEdit, setPoolForEdit] = useState<CashPool | null>(null);
  const [poolSearch, setPoolSearch] = useState('');
  const [poolProduct, setPoolProduct] = useState<CashPoolProduct>('gmra');

  const setShowFilterSidebar = useUIStore((state) => state.setShowFilterSidebar);
  const { searchQuery, setSearchQuery, filters } = useCounterpartyFilterStore();

  const debouncedSearch = useDebouncedValue(searchQuery.trim(), 250);
  const registerParams = useMemo(() => {
    const params: CounterpartyRegisterParams = {};
    if (debouncedSearch) params.search = debouncedSearch;
    // One value per server filter, so only a 1-of-N pick is sendable; with every
    // option picked, omitting is exact. Why that holds: counterparty-facets.
    const { statuses, agreementTypes } = filters;
    if (statuses.length === 1) params.is_active = statuses[0] === 'Active';
    if (agreementTypes.length === 1) params.agreement_type = agreementTypes[0];
    return params;
  }, [debouncedSearch, filters]);

  const {
    rows: counterparties,
    totalCount,
    isLoading,
    isError,
    error: registerError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCounterpartiesRegister(registerParams);
  const {
    data: custodians,
    isLoading: isCustodiansLoading,
    isError: isCustodiansError,
  } = useCustodianRegistry();
  const {
    data: pools,
    isLoading: isPoolsLoading,
    isError: isPoolsError,
  } = useCashPools({ product: poolProduct });

  // LR-584: registry writes are capability-gated — hide the create buttons and
  // the Edit columns from users without manage_registries (same hide pattern as
  // canTrade on the Trades page).
  const canManageRegistries = useAuth().hasPermission('manage_registries');
  // Pool writes need `create_trades` on the API (the rate a pool carries is what
  // LR-558 accrues on), so don't offer write affordances that end in a 403.
  const canManagePools = useAuth().hasPermission('create_trades');

  const columns = useMemo(
    () => createCounterpartyColumns(canManageRegistries ? setSelectedForEdit : undefined),
    [canManageRegistries],
  );
  const custodianColumns = useMemo(
    () => createCustodianColumns(canManageRegistries ? setSelectedCustodianForEdit : undefined),
    [canManageRegistries],
  );
  const poolColumns = useMemo(
    () => createCashPoolColumns(setPoolForEdit, canManagePools),
    [canManagePools],
  );

  const filteredCustodians = useMemo(() => {
    if (!custodians) return [];
    const query = custodianSearch.trim().toLowerCase();
    if (!query) return custodians;
    return custodians.filter(
      (c) => c.name.toLowerCase().includes(query) || c.short_code.toLowerCase().includes(query),
    );
  }, [custodians, custodianSearch]);

  const filteredPools = useMemo(() => {
    const rows = pools ?? [];
    const query = poolSearch.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter(
      (pool) =>
        pool.name.toLowerCase().includes(query) ||
        pool.counterparty_name.toLowerCase().includes(query) ||
        pool.currency.toLowerCase().includes(query),
    );
  }, [pools, poolSearch]);

  // The filter sidebar holds counterparty-only facets (agreement type / status),
  // so hide it on the custodian tab; restore it on unmount (LR-441).
  useEffect(() => {
    setShowFilterSidebar(activeTab === 'counterparties');
    return () => {
      setShowFilterSidebar(true);
    };
  }, [setShowFilterSidebar, activeTab]);

  const resultCount = searchQuery.trim() ? totalCount : undefined;
  const custodianResultCount = custodianSearch.trim() ? filteredCustodians.length : undefined;
  const poolResultCount = poolSearch.trim() ? filteredPools.length : undefined;

  return (
    <div className="relative flex h-[calc(100vh-0px)] flex-col">
      <TourSentinel name="counterparties-overview" />
      <Card className="flex flex-1 flex-col overflow-hidden rounded-none border-none">
        <CardHeader className="flex flex-col gap-2 py-2">
          <div className="flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-1" role="tablist" aria-label="Register">
              {REGISTER_TABS.map((tab, index) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  id={`register-tab-${tab}`}
                  aria-controls={`register-panel-${tab}`}
                  aria-selected={activeTab === tab}
                  tabIndex={activeTab === tab ? 0 : -1}
                  ref={(el) => {
                    tabRefs.current[index] = el;
                  }}
                  onKeyDown={(event) => handleTabKeyDown(event, index)}
                  onClick={() => setActiveTab(tab)}
                  className={clsx(
                    TAB_BUTTON_CLASSES,
                    activeTab === tab
                      ? 'text-gray-900 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700',
                  )}
                >
                  {REGISTER_TAB_LABELS[tab]}
                </button>
              ))}
            </div>

            {activeTab === 'counterparties' ? (
              <div className="flex items-center gap-3">
                <div className="w-80">
                  <SearchBar
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search counterparties..."
                    resultCount={resultCount}
                  />
                </div>

                {canManageRegistries && (
                  <button
                    data-tour="counterparty-create-button"
                    onClick={() => setIsAddModalOpen(true)}
                    className="flex items-center justify-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors whitespace-nowrap"
                  >
                    <PencilSquareIcon className="h-4 w-4 stroke-2" />
                    New Agreement
                  </button>
                )}

                <HelpLink to="counterparties" iconOnly label="Counterparties docs" />
              </div>
            ) : activeTab === 'custodians' ? (
              <div className="flex items-center gap-3">
                <div className="w-80">
                  <SearchBar
                    value={custodianSearch}
                    onChange={setCustodianSearch}
                    placeholder="Search custodians..."
                    resultCount={custodianResultCount}
                  />
                </div>

                {canManageRegistries && (
                  <button
                    onClick={() => setIsAddCustodianOpen(true)}
                    className="flex items-center justify-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors whitespace-nowrap"
                  >
                    <PencilSquareIcon className="h-4 w-4 stroke-2" />
                    New Custodian
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-80">
                  <SearchBar
                    value={poolSearch}
                    onChange={setPoolSearch}
                    placeholder="Search cash pools..."
                    resultCount={poolResultCount}
                  />
                </div>

                {canManagePools && (
                  <button
                    onClick={() => setIsCreatePoolOpen(true)}
                    className="flex items-center justify-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors whitespace-nowrap"
                  >
                    <PencilSquareIcon className="h-4 w-4 stroke-2" />
                    New Cash Pool
                  </button>
                )}
              </div>
            )}
          </div>

          {/* GMRA and GMSLA pools are legally separate and never mix — the
              register keeps the same segment strip it had in Collateral Manager. */}
          {activeTab === 'cash-pools' && (
            <ProductTabs active={poolProduct} onChange={setPoolProduct} />
          )}
        </CardHeader>

        <CardContent
          role="tabpanel"
          id={`register-panel-${activeTab}`}
          aria-labelledby={`register-tab-${activeTab}`}
          className="flex flex-1 flex-col overflow-hidden p-0 border-t border-gray-200 relative min-h-0"
        >
          {activeTab === 'counterparties' ? (
            <CounterpartyTable
              data={counterparties}
              columns={columns}
              isLoading={isLoading}
              // Gated on loaded rows: a failed refresh or further page leaves
              // isError true over cached ones, and blanking a populated table is
              // worse than the lie the branch prevents.
              isError={isError && counterparties.length === 0}
              error={registerError}
              onLoadMore={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              loaderResetKey={registerParams}
            />
          ) : activeTab === 'custodians' ? (
            <CustodianTable
              data={filteredCustodians}
              columns={custodianColumns}
              isLoading={isCustodiansLoading}
              isError={isCustodiansError && (custodians?.length ?? 0) === 0}
            />
          ) : isPoolsError && (pools?.length ?? 0) === 0 ? (
            // A failed fetch must not read as "this desk has no pools": the empty
            // state invites the trader to create one, and the duplicate warning is
            // computed from this same list, so silence would look exactly like
            // "nothing matches".
            // Gated on the RAW list being empty, like the two tabs above (LR-625):
            // this query refetches on window focus, so a failed REFRESH sets
            // isError while rows are still cached — and replacing a populated
            // register with the banner loses what the operator was reading.
            <div
              role="alert"
              className="flex flex-1 items-center justify-center p-10 text-sm text-red-700"
            >
              Couldn&apos;t load cash pools. Retry before creating one - existing
              pools can&apos;t be checked while this list is unavailable.
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col overflow-auto">
              <CashPoolTable
                data={filteredPools}
                columns={poolColumns}
                isLoading={isPoolsLoading}
                emptyMessage={poolSearch.trim() ? 'No cash pools found.' : undefined}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {isAddModalOpen && (
        <AddCounterpartyAgreementModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
        />
      )}

      {selectedForEdit && (
        <AddCounterpartyAgreementModal
          isOpen={!!selectedForEdit}
          onClose={() => setSelectedForEdit(null)}
          mode="edit"
          counterparty={selectedForEdit}
        />
      )}

      {isAddCustodianOpen && (
        <AddCustodianModal
          isOpen={isAddCustodianOpen}
          onClose={() => setIsAddCustodianOpen(false)}
        />
      )}

      {selectedCustodianForEdit && (
        <AddCustodianModal
          isOpen={!!selectedCustodianForEdit}
          onClose={() => setSelectedCustodianForEdit(null)}
          mode="edit"
          custodian={selectedCustodianForEdit}
        />
      )}

      {isCreatePoolOpen && (
        <CashPoolModal
          isOpen={isCreatePoolOpen}
          onClose={() => setIsCreatePoolOpen(false)}
          product={poolProduct}
          pools={pools ?? []}
          poolsUnavailable={isPoolsError}
        />
      )}

      {poolForEdit && (
        <CashPoolModal
          isOpen={!!poolForEdit}
          onClose={() => setPoolForEdit(null)}
          product={poolForEdit.product}
          pools={pools ?? []}
          poolsUnavailable={isPoolsError}
          mode="edit"
          pool={poolForEdit}
        />
      )}
    </div>
  );
};
